import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, InputAdornment, TextField } from '@material-ui/core';
import { useStyles } from '../login/LoginPage';
import authService from '../../../services/AuthService/AuthService';
import { useSetRecoilState } from 'recoil';
import { alertState } from '../../../recoil-store/recoil-state';
import CircularProgress from "@material-ui/core/CircularProgress";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import Validations from "../../../home/pages/user/parts/formComponents/passwordValidations";
import PasswordValidationPane from "../../../components/PasswordValidationPane/PasswordValidationPane";

/**
 * @Description This function lets you return Reset Password page.
 * @Returns     JSX.Element
 * @Author      ibrahim AKIN
 */
function ResetPassword(props) {
    const location = useLocation();
    const passwordInputRef = useRef(null);
    const confirmPasswordInputRef = useRef(null);
    const hash = location.pathname.split('/').pop();
    const classes = useStyles();
    const { t } = useTranslation();
    const label = 'CONTENT.AUTH-PAGES.';
    const [focusedInput, setFocusedInput] = useState(null);
    const [password, setPassword] = useState();
    const [passwordValid, setPasswordValid] = useState(false);
    const [confirmPassword, setConfirmPassword] = useState();
    const [showPassword, setShowPassword] = useState(false);
    const [position, setPosition] = useState({});
    const [loading, setLoading] = useState(false);
    const userId = useRef('');
    const setAlert = useSetRecoilState(alertState);


    const history = useNavigate();
    useEffect(() => { fetchData(); }, []);

    useEffect(() => {
        if (focusedInput === 'password') {
            const rect = passwordInputRef.current.getBoundingClientRect();
            setPosition({
                top: `${rect.bottom + window.scrollY}px`,
                left: `${rect.left + window.scrollX}px`
            });
            document.getElementById("message").style.display = "block";
            if (password) {
                Validations.passwordValidations(password)
            }
        } else if (focusedInput === 'confirm_password') {
            const rect = confirmPasswordInputRef.current.getBoundingClientRect();
            setPosition({
                top: `${rect.bottom + window.scrollY}px`,
                left: `${rect.left + window.scrollX}px`
            });
            document.getElementById("message").style.display = "block";
            if (passwordValid && confirmPassword) {
                Validations.passwordValidations(password, confirmPassword)
            }
        } else {
            const passwordValidationEl = document.getElementById("message");
            if (passwordValidationEl) {
                passwordValidationEl.style.display = "block";
            }
        }
    }, [focusedInput])

    const fetchData = async () => {
        const response = await authService.verifyResetPassword(hash);
        if (!response) {
            setAlert(alert => ({ ...alert, show: true, message: t('GENERAL.NETWORK-ERROR'), severity: 'error' }))
        } else if (response.status) {
            userId.current = response.data.user_id;
            setLoading(false);
        } else {
            redirectLogin();
            let message = t('MESSAGES.AUTH-PAGES.RESET-PAGE.' + response.message);
            let severity = response.status ? 'success' : 'error';
            setAlert(alert => ({ ...alert, show: true, message: message, severity: severity }))
        }
    }
    const redirectLogin = () => {
        setTimeout(() => { history('/login'); }, 2000);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const response = await authService.resetPassword({
            confirmPassword: e.target.confirmPassword.value,
            newPassword: e.target.newPassword.value,
            hashCode: hash,
            userId: userId.current
        });
        if (response) {
            let message = t('MESSAGES.AUTH-PAGES.RESET-PAGE.' + response.message);
            let severity = response.status ? 'success' : 'error';
            setAlert(alert => ({ ...alert, show: true, message: message, severity: severity }))
            if (response.status) { redirectLogin(); }
        } else {
            setAlert(alert => ({ ...alert, show: true, message: t('GENERAL.NETWORK-ERROR'), severity: 'error' }))
        }
    };

    const handleShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handlePaste = (event) => {
        event.preventDefault();
    };

    const handleChange = (field, event) => {
        const value = event.target.value;
        if (field === "password") {
            setPassword(value)
            setPasswordValid(Validations.passwordValidations(value))
        } else if (field === "confirm_password") {
            setConfirmPassword(value)
            Validations.passwordValidations(password, value)
        }
    }

    const onFocus = (field) => {
        setFocusedInput(field);
    }

    return (
        <div className="login-page">
            <form onSubmit={handleSubmit} className="login-container forgot">
                <div className="login-autommate"></div>
                <div></div>
                <div className="login-seperator"><div></div><div>{t(label + 'RESET-PASSWORD-HEADER')}</div><div></div></div>
                <div>
                    <div className="login-label">
                        <div>{t(label + 'RESET-PASSWORD-TITLE')} *</div>
                        <TextField
                            ref={passwordInputRef}
                            id="pass-field"
                            classes={{ root: classes.text }}
                            variant="outlined"
                            fullWidth
                            onChange={(e) => handleChange("password", e)}
                            onFocus={() => onFocus("password")}
                            onBlur={() => { setFocusedInput(null) }}
                            name="newPassword"
                            type={showPassword ? 'text' : 'password'}
                            placeholder={t(label + 'NEW-PASSWORD')}
                            InputProps={{
                                endAdornment:
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={handleShowPassword}>
                                            {showPassword ? <Visibility fontSize="inherit" /> :
                                                <VisibilityOff fontSize="inherit" />}
                                        </IconButton>
                                    </InputAdornment>
                            }}
                        />
                    </div>
                    <div className="login-label">
                        <div>{t(label + 'RESET-PASSWORD-AGAIN')} *</div>
                        <TextField
                            InputProps={{
                                style: {
                                    borderColor: 'red',
                                }
                            }}
                            ref={confirmPasswordInputRef}
                            id="confirm-pass-field"
                            onFocus={() => { onFocus("confirm_password") }}
                            onBlur={() => { setFocusedInput(null) }}
                            classes={{ root: classes.text }}
                            variant="outlined"
                            fullWidth name="confirmPassword"
                            placeholder={t(label + 'NEW-PASSWORD-AGAIN')}
                            type="password"
                            onPaste={handlePaste}
                            onChange={(e) => handleChange("confirm_password", e)}
                        />
                    </div>
                    {focusedInput && <PasswordValidationPane style={position} field={focusedInput} />}
                    <div className="login-option">
                        <div></div>
                        <Link to="/login">{t(label + 'BACK-TO-LOGIN')}</Link>
                    </div>
                </div>
                <Button endIcon={loading ? <CircularProgress style={{ color: "#fff" }} size={20} /> : null}
                    style={{ backgroundColor: '#031226', color: '#fff', width: '100%', padding: '9px 0' }}
                    disabled={loading || !password || !confirmPassword} type="submit">
                    {!loading && <span className="login-signin">{t(label + 'CHANGE-PASSWORD')}</span>}
                </Button>
            </form>
            <div className="login-right">
                <div>{t(label + 'AUTOMATE')}</div>
                <div>{t(label + 'WITHOUT-CODE')}</div>
                <div>{t(label + 'INTEGRATING')}</div>
            </div>
        </div>
    );
}

export default ResetPassword;